import type { InviteCodeRow } from "@/auth/api/invite-api";
import { INVITE_ACCESS_LEVEL_LABELS } from "@/auth/permissions";
import { Badge, Button } from "@/components/ui";
import type { ColumnDef } from "@tanstack/react-table";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function statusBadge(row: InviteCodeRow) {
  if (row.revoked_at) return <Badge tone="danger">Revoked</Badge>;
  if (!row.active) return <Badge tone="neutral">Inactive</Badge>;
  return <Badge tone="success">Active</Badge>;
}

export interface InviteCodeTableActions {
  revokingId: string | null;
  onRevoke: (id: string) => void;
}

export function inviteCodeColumns(
  actions: InviteCodeTableActions,
): ColumnDef<InviteCodeRow, unknown>[] {
  return [
    {
      id: "code",
      accessorKey: "code",
      header: "Code",
      cell: ({ row }) => <span className="font-mono tracking-wide">{row.original.code}</span>,
    },
    {
      id: "access_level",
      accessorKey: "access_level",
      header: "Access",
      cell: ({ row }) => INVITE_ACCESS_LEVEL_LABELS[row.original.access_level],
    },
    {
      id: "uses",
      header: "Uses",
      cell: ({ row }) => `${row.original.use_count}/${row.original.max_uses}`,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => statusBadge(row.original),
    },
    {
      id: "note",
      accessorKey: "note",
      header: "Note",
      cell: ({ getValue }) => (
        <span className="text-[var(--color-text-muted)]">{(getValue() as string) ?? "—"}</span>
      ),
    },
    {
      id: "expires_at",
      accessorKey: "expires_at",
      header: "Expires",
      cell: ({ getValue }) => (
        <span className="text-[var(--color-text-muted)]">
          {formatDate(getValue() as string | null)}
        </span>
      ),
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => (
        <span className="text-[var(--color-text-muted)]">
          {formatDate(getValue() as string | null)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.revoked_at ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={actions.revokingId === row.original.id}
            onClick={() => actions.onRevoke(row.original.id)}
          >
            {actions.revokingId === row.original.id ? "Revoking…" : "Revoke"}
          </Button>
        ),
    },
  ];
}
